import { useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, SectionScaffold, TablePageShell } from '@/shared/ui';

import { useDeleteCategory, useSearchCategories } from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { formatNumber } from '../../lib/format';
import { useToast } from '../../lib/useToast';
import type { CategorySummary } from '../../model/types';
import { CategoryFormDialog } from '../components/CategoryFormDialog';

/** Catalog categories — list + create/edit. */
export function CategoriesPage() {
  const { canView, canManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [filter, setFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CategorySummary | null>(null);
  const [deleting, setDeleting] = useState<CategorySummary | null>(null);

  const query = useSearchCategories(
    { query: filter.trim() || undefined, page: 0, size: 100, sort: 'name,asc' },
    { enabled: canView },
  );
  const deleteMutation = useDeleteCategory();

  const categories = useMemo(() => query.data?.content ?? [], [query.data]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (category: CategorySummary) => {
    setEditing(category);
    setDialogOpen(true);
  };
  const confirmDelete = () => {
    if (!deleting) return;
    const name = deleting.name;
    deleteMutation.mutate(deleting.id, {
      onSuccess: () => {
        success(`Категория «${name}» удалена`);
        setDeleting(null);
      },
      onError: (e) => {
        notifyError(e.message ?? 'Не удалось удалить категорию');
        setDeleting(null);
      },
    });
  };

  return (
    <SectionScaffold
      title="Категории"
      subtitle="Структура категорий каталога вашего магазина."
      canView={canView}
      settingsTo="/settings/catalog"
      settingsLabel="Настройки каталога"
      search={{ value: filter, onChange: setFilter, placeholder: 'Поиск по названию или слагу…' }}
      primaryAction={
        canManage ? (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
            Добавить категорию
          </Button>
        ) : undefined
      }
    >
      <TablePageShell>
        {query.isLoading ? (
          <LoadingState label="Загрузка категорий…" />
        ) : query.isError ? (
          <ErrorState
            title="Не удалось загрузить категории"
            message={query.error?.message ?? 'Произошла ошибка.'}
            onRetry={() => void query.refetch()}
          />
        ) : categories.length === 0 ? (
          <EmptyState
            title={filter ? 'Ничего не найдено' : 'Категорий пока нет'}
            description={filter ? 'Измените запрос.' : 'Создайте первую категорию каталога.'}
            icon={<CategoryRoundedIcon />}
            action={
              canManage && !filter ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                  Добавить категорию
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Категория</TableCell>
                  <TableCell>Слаг</TableCell>
                  <TableCell>Код</TableCell>
                  <TableCell align="right">Товаров</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {category.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}
                      >
                        {category.slug}
                      </Box>
                    </TableCell>
                    <TableCell>{category.code ?? '—'}</TableCell>
                    <TableCell align="right">{formatNumber(category.productCount)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={canManage ? 'Редактировать' : 'Просмотр'}>
                        <IconButton
                          size="small"
                          color="primary"
                          aria-label={`Редактировать: ${category.name}`}
                          onClick={() => openEdit(category)}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canManage && (
                        <Tooltip title="Удалить">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Удалить: ${category.name}`}
                            onClick={() => setDeleting(category)}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TablePageShell>

      <Dialog open={deleting !== null} onClose={() => setDeleting(null)}>
        <DialogTitle>Удалить категорию?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Категория «{deleting?.name}» будет удалена. Если в ней есть товары, удаление будет
            отклонено — сначала перенесите или удалите товары.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Отмена</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      <CategoryFormDialog
        open={dialogOpen}
        category={editing}
        onClose={() => setDialogOpen(false)}
        onResult={(severity, message) =>
          severity === 'success' ? success(message) : notifyError(message)
        }
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </SectionScaffold>
  );
}
